package uk.co.nickthecoder.kyd.util

import org.khronos.webgl.Float32Array
import org.khronos.webgl.WebGLProgram
import org.khronos.webgl.WebGLRenderingContext
import org.khronos.webgl.WebGLRenderingContext.Companion.ARRAY_BUFFER
import org.khronos.webgl.WebGLRenderingContext.Companion.COMPILE_STATUS
import org.khronos.webgl.WebGLRenderingContext.Companion.DYNAMIC_DRAW
import org.khronos.webgl.WebGLRenderingContext.Companion.FLOAT
import org.khronos.webgl.WebGLRenderingContext.Companion.FRAGMENT_SHADER
import org.khronos.webgl.WebGLRenderingContext.Companion.STATIC_DRAW
import org.khronos.webgl.WebGLRenderingContext.Companion.TEXTURE_2D
import org.khronos.webgl.WebGLRenderingContext.Companion.TRIANGLE_STRIP
import org.khronos.webgl.WebGLRenderingContext.Companion.VERTEX_SHADER
import org.khronos.webgl.WebGLTexture
import uk.co.nickthecoder.glok.backend.backend
import uk.co.nickthecoder.glok.backend.webgl.WebGLBackend
import uk.co.nickthecoder.glok.util.log

/**
 * A Shader program which blits axis-aligned rectangular area of a Texture.
 * There are no transformation matrices - the API uses to -1 to 1 coordinates for the destination
 * and 0..1 for the texture's coordinates.
 */
object PlainTextureProgram {

    private val shaderProgram: WebGLProgram = with(WebGLBackend.gl) {
        createProgram().also { program ->
            val vertexShader = createShader(VERTEX_SHADER).also { shader ->
                shaderSource(shader, VERTEX_SHADER_CODE)
                compileShader(shader)
                if (getShaderParameter(shader, COMPILE_STATUS) != true) {
                    log.error("PlainTextureProgram.vertex shader failed : '${getShaderInfoLog(shader)}'")
                }
            }
            val fragmentShader = createShader(FRAGMENT_SHADER).also { shader ->
                shaderSource(shader, FRAGMENT_SHADER_CODE)
                compileShader(shader)
                if (getShaderParameter(shader, COMPILE_STATUS) != true) {
                    log.error("PlainTextureProgram.fragment shader failed : '${getShaderInfoLog(shader)}'")
                }
            }
            attachShader(program, vertexShader)
            attachShader(program, fragmentShader)
            linkProgram(program)
            if (getProgramParameter(program, WebGLRenderingContext.LINK_STATUS) != true) {
                log.error("Link of PlainTextureProgram shader failed")
            } else {
                log.info("Compiled PlainTextureProgram")
            }
            deleteShader(vertexShader)
            deleteShader(fragmentShader)
        } !!
    }

    private val positionLocation = WebGLBackend.gl.getAttribLocation(shaderProgram, "position")
    private val uvLocation = WebGLBackend.gl.getAttribLocation(shaderProgram, "uv")

    /**
     * Used by [blitAll], where a texture is applied to the entire `destination`
     * (which is usually a frame buffer object, not a window).
     */
    private val wholeViewVertexBuffer = with(WebGLBackend.gl) {
        createBuffer().apply {
            val data = arrayOf<Float>(
                - 1f, 1f, 0f, 1f,
                1f, 1f, 1f, 1f,
                - 1f, - 1f, 0f, 0f,

                1f, - 1f, 1f, 0f
            )
            bindBuffer(ARRAY_BUFFER, this)
            bufferData(ARRAY_BUFFER, Float32Array(data), STATIC_DRAW)
        }
    }

    init {
        backend.reportError()
    }

    /**
     * The coordinates are not transformed (no view or model matrices).
     *
     * [left], [top], [right] and [bottom] are in the range -1 to 1, where -1, -1 is the bottom left.
     *
     * [tLeft], [tTop], [tRight], [tBottom] are in the range 0 to 0, where 0,1 is the bottom left.
     */
    //
    fun blit(
        left: Float, top: Float, right: Float, bottom: Float,
        textureID: WebGLTexture,
        tLeft: Float, tTop: Float, tRight: Float, tBottom: Float
    ) {
        with(WebGLBackend.gl) {
            //println("blit $left , $top .. $right , $bottom")
            //println("from $tLeft, $tTop .. $tRight , $tBottom")

            useProgram(shaderProgram)

            val data = arrayOf<Float>(
                left, top, tLeft, tTop,
                right, top, tRight, tTop,
                left, bottom, tLeft, tBottom,

                right, bottom, tRight, tBottom
            )

            bindTexture(TEXTURE_2D, textureID)

            val buffer = createBuffer()
            bindBuffer(ARRAY_BUFFER, buffer)
            bufferData(ARRAY_BUFFER, Float32Array(data), STATIC_DRAW)

            vertexAttribPointer(positionLocation, 2, FLOAT, false, 16, 0)
            vertexAttribPointer(uvLocation, 2, FLOAT, false, 16, 8)
            enableVertexAttribArray(positionLocation)
            enableVertexAttribArray(uvLocation)

            drawArrays(TRIANGLE_STRIP, 0, 4)

            bindBuffer(ARRAY_BUFFER, null)
            deleteBuffer(buffer)

            disableVertexAttribArray(positionLocation)
            disableVertexAttribArray(uvLocation)
            useProgram(null)
        }
    }


    /**
     * Blits the texture to entire area of the destination.
     * This is identical to (but faster than) :
     *
     *     blit( textureID, -1f, 1f,  1f, -1f,  0f, 1f,  1f, 0f )
     */
    fun blitAll(textureID: WebGLTexture) {

        with(WebGLBackend.gl) {
            useProgram(shaderProgram)
            bindTexture(TEXTURE_2D, textureID)
            bindBuffer(ARRAY_BUFFER, wholeViewVertexBuffer)

            vertexAttribPointer(positionLocation, 2, FLOAT, false, 16, 0)
            vertexAttribPointer(uvLocation, 2, FLOAT, false, 16, 8)
            enableVertexAttribArray(positionLocation)
            enableVertexAttribArray(uvLocation)

            drawArrays(TRIANGLE_STRIP, 0, 4)

            bindBuffer(ARRAY_BUFFER, null)
            disableVertexAttribArray(positionLocation)
            disableVertexAttribArray(uvLocation)
            useProgram(null)
        }
    }

}

private const val VERTEX_SHADER_CODE = """
attribute vec2 position;
attribute vec2 uv;

varying vec2 textureCoord;

void main() {
    textureCoord = uv;

    gl_Position = vec4( position, 0.0, 1.0);
}    
"""
private const val FRAGMENT_SHADER_CODE = """
precision highp float;

varying vec2 textureCoord;

uniform sampler2D texImage;

void main() {
    gl_FragColor = texture2D(texImage, textureCoord);
}    
"""
